<template>
  <div
    class="TaskRunView"
    :class="{
      'TaskRunView--with-message': item.message || item.commit_message,
      'TaskRunView--stopped': isTaskStopped,
    }"
  >
    <div v-if="item.message || item.commit_message" class="TaskRunView__message">
      <span v-if="item.message">
        <ZpIcon name="message-square" :size="14" />
        {{ item.message }}
      </span>
      <span v-if="item.commit_message">
        <ZpIcon name="git-fork" :size="14" />
        {{ item.commit_message }}
      </span>
    </div>

    <div class="TaskRunView__status-strip">
      <TaskStatus :status="item.status" data-testid="task-status" />
      <span class="TaskRunView__status-chip">
        <ZpIcon name="user" :size="14" />
        {{ userLabel }}
      </span>
      <span class="TaskRunView__status-chip">
        <ZpIcon name="clock" :size="14" />
        {{ durationLabel }}
      </span>
      <span class="TaskRunView__status-chip TaskRunView__status-chip--socket">
        <span class="TaskRunView__socket-dot"></span>
        {{ $t('taskLogsStreaming') }}
      </span>
    </div>

    <div class="TaskRunView__tabs" role="tablist">
      <button
        v-for="tabItem in tabs"
        :key="tabItem.key"
        class="TaskRunView__tab"
        :class="{ 'is-active': tab === tabItem.value }"
        type="button"
        @click="tab = tabItem.value"
      >
        <ZpIcon :name="tabItem.icon" :size="15" />
        {{ tabItem.label }}
      </button>
    </div>

    <section v-if="tab === 0" class="TaskRunView__log">
      <aside class="TaskRunTimeline">
        <span class="zp-overline">{{ $t('executionTimeline') }}</span>
        <div class="TaskRunTimeline__track">
          <div
            v-for="stage in executionStages"
            :key="stage.key"
            class="TaskRunTimeline__stage"
            :class="`is-${stage.variant}`"
          >
            <span class="TaskRunTimeline__glyph">
              <ZpIcon :name="stage.icon" :size="14" />
            </span>
            <span class="TaskRunTimeline__body">
              <strong>{{ stage.label }}</strong>
              <small>{{ stage.caption }}</small>
            </span>
            <span class="TaskRunTimeline__time zp-tabular">{{ stage.time }}</span>
          </div>
        </div>
      </aside>

      <main class="TaskRunConsole">
        <div class="TaskRunConsole__toolbar">
          <label class="TaskRunConsole__search" for="task-log-search">
            <ZpIcon name="search" :size="15" />
            <input
              id="task-log-search"
              v-model="logSearch"
              :aria-label="$t('searchLogs')"
              :placeholder="$t('searchLogs')"
              type="search"
            />
          </label>

          <select v-model="levelFilter" class="TaskRunConsole__select">
            <option value="all">{{ $t('allLevels') }}</option>
            <option value="info">INFO</option>
            <option value="task">TASK</option>
            <option value="ok">OK</option>
            <option value="error">ERROR</option>
          </select>

          <button
            type="button"
            class="TaskRunConsole__tool"
            :class="{ 'is-active': wrapLogs }"
            @click="wrapLogs = !wrapLogs"
          >
            <ZpIcon name="wrap-text" :size="15" />
            {{ $t('wrap') }}
          </button>

          <button
            type="button"
            class="TaskRunConsole__tool"
            :class="{ 'is-active': paused }"
            @click="paused = !paused"
          >
            <ZpIcon :name="paused ? 'play' : 'pause'" :size="15" />
            {{ paused ? $t('resume') : $t('pause') }}
          </button>

          <button
            type="button"
            class="TaskRunConsole__tool"
            @click="logSearch = ''; levelFilter = 'all'"
          >
            {{ $t('clear') }}
          </button>
        </div>

        <VirtualList
          class="task-log-records"
          :class="{ 'task-log-records--wrap': wrapLogs }"
          :data-key="'id'"
          :data-sources="filteredOutput"
          :data-component="itemComponent"
          :estimate-size="22"
          :keeps="100"
          ref="records"
          @scroll.native="onRecordsScroll"
        >
          <div
            class="task-log-records__record"
            v-for="record in filteredOutput"
            :key="record.id"
            :class="`is-${logLevel(record)}`"
          >
            <div class="task-log-records__time">
              {{ record.time | formatTime }}
            </div>
            <div
              class="task-log-records__output"
              v-html="$options.filters.formatLog(record.output)"
            ></div>
          </div>
        </VirtualList>

        <div class="TaskRunConsole__footer">
          <span>
            <span class="TaskRunView__socket-dot"></span>
            {{ autoScroll ? $t('autoScrollOn') : $t('taskLogsStreaming') }}
          </span>
          <span class="zp-tabular">
            {{ filteredOutput.length }} / {{ output.length }}
          </span>
        </div>
      </main>

      <aside class="TaskRunTelemetry">
        <span class="zp-overline">{{ $t('runTelemetry') }}</span>
        <div class="TaskRunTelemetry__status">
          <TaskStatus :status="item.status" />
          <div>
            <span>{{ $t('started') }}</span>
            <b class="zp-tabular">{{ startTimeLabel }}</b>
          </div>
          <div>
            <span>{{ $t('duration') }}</span>
            <b class="zp-tabular">{{ durationLabel }}</b>
          </div>
        </div>
        <div class="TaskRunTelemetry__rows">
          <div v-for="row in telemetryRows" :key="row.label">
            <span>
              <ZpIcon :name="row.icon" :size="14" />
              {{ row.label }}
            </span>
            <b :class="{ 'zp-tabular': row.mono }">{{ row.value }}</b>
          </div>
        </div>
      </aside>

      <div class="TaskRunView__actions">
        <span class="TaskRunView__connection">
          <span class="TaskRunView__socket-dot"></span>
          {{ $t('taskLogsConnected') }}
        </span>
        <div>
          <v-btn
            v-if="item.status === 'waiting_confirmation'"
            color="success"
            depressed
            @click="confirmTask()"
          >
            <ZpIcon name="check" :size="18" class="mr-1" />
            {{ $t('confirmTask') }}
          </v-btn>
          <v-btn
            v-if="item.status === 'waiting_confirmation'"
            color="warning"
            depressed
            @click="rejectTask()"
          >
            <ZpIcon name="x" :size="18" class="mr-1" />
            {{ $t('reject') }}
          </v-btn>
          <v-btn
            v-if="canStop"
            color="error"
            depressed
            @click="stopTask(item.status === 'stopping')"
          >
            <ZpIcon name="square" :size="16" class="mr-1" />
            {{ item.status === 'stopping' ? $t('forceStop') : $t('stop') }}
          </v-btn>
          <v-btn
            v-if="isTaskStopped"
            color="blue-grey"
            depressed
            :href="rawLogURL"
            target="_blank"
            data-testid="task-rawLog"
          >
            <ZpIcon name="download" :size="16" class="mr-1" />
            {{ $t('raw_log') }}
          </v-btn>
        </div>
      </div>
    </section>

    <section v-else-if="tab === 1" class="TaskRunView__tab-panel">
      <TaskDetails
        :item="item"
        :user="user"
        :project-id="projectId"
        :template-context="template"
      />
    </section>

    <section v-else-if="tab === 2" class="TaskRunView__tab-panel">
      <AnsibleStageView
        :premium-features="systemInfo.premium_features"
        :project-id="projectId"
        :task-id="itemId"
      />
    </section>
  </div>
</template>

<style lang="scss">
.TaskRunView {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;
  color: var(--zp-text-primary);

  &__message {
    display: flex;
    gap: var(--zp-space-md);
    padding: var(--zp-space-sm) var(--zp-space-lg);
    border-bottom: var(--zp-border-hair) solid var(--zp-border-hairline);
    background: var(--zp-bg-surface);
    color: var(--zp-text-secondary);
    font-size: var(--zp-text-xs);

    span {
      display: inline-flex;
      align-items: center;
      gap: var(--zp-space-2xs);
      min-width: 0;
    }
  }

  &__status-strip {
    display: flex;
    align-items: center;
    gap: var(--zp-space-sm);
    padding: var(--zp-space-sm) var(--zp-space-lg);
    border-bottom: var(--zp-border-hair) solid var(--zp-border-hairline);
    background: var(--zp-bg-page);
    overflow-x: auto;
  }

  &__status-chip {
    display: inline-flex;
    align-items: center;
    gap: var(--zp-space-2xs);
    min-height: 26px;
    padding: 0 var(--zp-space-sm);
    border: var(--zp-border-hair) solid var(--zp-border-hairline);
    border-radius: var(--zp-radius-md);
    background: var(--zp-bg-surface);
    color: var(--zp-text-secondary);
    font-size: var(--zp-text-xs);
    white-space: nowrap;
  }

  &__socket-dot {
    display: inline-block;
    width: 7px;
    height: 7px;
    border-radius: var(--zp-radius-pill);
    background: var(--zp-status-success);
  }

  &__tabs {
    display: flex;
    min-height: 48px;
    border-bottom: var(--zp-border-hair) solid var(--zp-border-hairline);
    background: var(--zp-bg-surface);
  }

  &__tab {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--zp-space-2xs);
    min-width: 132px;
    height: 48px;
    border: 0;
    border-bottom: 2px solid transparent;
    background: transparent;
    color: var(--zp-text-secondary);
    font-size: var(--zp-text-sm);
    font-weight: 700;
    cursor: pointer;

    &.is-active {
      border-bottom-color: var(--zp-brand);
      color: var(--zp-brand);
    }
  }

  &__log {
    display: grid;
    grid-template-columns: 280px minmax(0, 1fr) 300px;
    grid-template-rows: minmax(0, 1fr) auto;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
  }

  &__tab-panel {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    padding: var(--zp-space-md);
  }

  &__actions {
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--zp-space-md);
    min-height: 68px;
    padding: var(--zp-space-sm) var(--zp-space-lg);
    border-top: var(--zp-border-hair) solid var(--zp-border-hairline);
    background: var(--zp-bg-surface);

    > div {
      display: flex;
      gap: var(--zp-space-sm);
    }
  }

  &__connection {
    display: inline-flex;
    align-items: center;
    gap: var(--zp-space-xs);
    color: var(--zp-text-secondary);
    font-size: var(--zp-text-xs);
  }
}

.TaskRunTimeline {
  min-height: 0;
  padding: var(--zp-space-lg);
  border-right: var(--zp-border-hair) solid var(--zp-border-hairline);
  background: var(--zp-bg-page);

  &__track {
    display: grid;
    gap: var(--zp-space-lg);
    margin-top: var(--zp-space-lg);
  }

  &__stage {
    position: relative;
    display: grid;
    grid-template-columns: 28px minmax(0, 1fr) auto;
    gap: var(--zp-space-xs);
    align-items: start;

    &::before {
      content: '';
      position: absolute;
      top: 27px;
      left: 11px;
      width: var(--zp-border-hair);
      height: calc(100% + var(--zp-space-lg) - 18px);
      background: var(--zp-border-default);
    }

    &:last-child::before {
      display: none;
    }

    &.is-success .TaskRunTimeline__glyph {
      background: var(--zp-status-success-bg);
      color: var(--zp-status-success);
    }

    &.is-running .TaskRunTimeline__glyph {
      background: var(--zp-status-running-bg);
      color: var(--zp-status-running);
    }

    &.is-danger .TaskRunTimeline__glyph {
      background: var(--zp-status-danger-bg);
      color: var(--zp-status-danger);
    }
  }

  &__glyph {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: var(--zp-radius-pill);
    background: var(--zp-status-neutral-bg);
    color: var(--zp-status-neutral);
    z-index: 1;
  }

  &__body {
    min-width: 0;

    strong,
    small {
      display: block;
      line-height: var(--zp-leading-snug);
    }

    strong {
      color: var(--zp-text-primary);
      font-size: var(--zp-text-sm);
      font-weight: 700;
    }

    small {
      margin-top: 2px;
      color: var(--zp-text-tertiary);
      font-size: var(--zp-text-xs);
    }
  }

  &__time {
    color: var(--zp-text-tertiary);
    font-size: var(--zp-text-xs);
  }
}

.TaskRunConsole {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  background: oklch(16% 0.018 268);

  &__toolbar {
    display: flex;
    align-items: center;
    gap: var(--zp-space-sm);
    padding: var(--zp-space-sm);
    border-bottom: var(--zp-border-hair) solid oklch(26% 0.022 268);
    background: oklch(18% 0.018 268);
  }

  &__search {
    display: flex;
    align-items: center;
    gap: var(--zp-space-xs);
    flex: 1 1 220px;
    min-width: 160px;
    height: 34px;
    padding: 0 var(--zp-space-sm);
    border: var(--zp-border-hair) solid oklch(31% 0.026 268);
    border-radius: var(--zp-radius-md);
    color: oklch(72% 0.038 268);

    input {
      width: 100%;
      min-width: 0;
      border: 0;
      outline: 0;
      background: transparent;
      color: oklch(92% 0.018 268);
      font-family: var(--zp-font-body);
      font-size: var(--zp-text-sm);
    }
  }

  &__select,
  &__tool {
    height: 34px;
    border: var(--zp-border-hair) solid oklch(31% 0.026 268);
    border-radius: var(--zp-radius-md);
    background: transparent;
    color: oklch(88% 0.018 268);
    font-size: var(--zp-text-xs);
  }

  &__select {
    min-width: 116px;
    padding: 0 var(--zp-space-sm);
  }

  &__tool {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--zp-space-2xs);
    padding: 0 var(--zp-space-sm);
    cursor: pointer;

    &.is-active {
      border-color: var(--zp-brand);
      color: oklch(93% 0.042 268);
      background: oklch(24% 0.05 268);
    }
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    gap: var(--zp-space-md);
    padding: var(--zp-space-xs) var(--zp-space-sm);
    border-top: var(--zp-border-hair) solid oklch(26% 0.022 268);
    color: oklch(74% 0.022 268);
    font-size: var(--zp-text-xs);

    span {
      display: inline-flex;
      align-items: center;
      gap: var(--zp-space-xs);
    }
  }
}

.task-log-records {
  flex: 1 1 auto;
  min-height: 360px;
  margin: 0;
  padding: var(--zp-space-sm) var(--zp-space-sm) var(--zp-space-lg);
  overflow: auto;
  color: oklch(91% 0.018 268);
  font-family: var(--zp-font-mono);
  font-size: 12px;
  line-height: 1.55;
}

.task-log-records__record {
  display: flex;
  flex-direction: row;
  justify-content: left;

  &.is-ok .task-log-records__output {
    color: oklch(72% 0.15 145);
  }

  &.is-error .task-log-records__output {
    color: oklch(70% 0.14 25);
  }

  &.is-task .task-log-records__output {
    color: oklch(76% 0.09 268);
  }
}

.task-log-records__time {
  width: 86px;
  min-width: 86px;
  color: oklch(62% 0.025 268);
}

.task-log-records__output {
  width: 100%;
  white-space: pre;
}

.task-log-records--wrap .task-log-records__output {
  white-space: pre-wrap;
}

.TaskRunTelemetry {
  min-height: 0;
  padding: var(--zp-space-lg);
  border-left: var(--zp-border-hair) solid var(--zp-border-hairline);
  background: var(--zp-bg-page);

  &__status {
    display: grid;
    gap: var(--zp-space-xs);
    margin-top: var(--zp-space-md);
    padding: var(--zp-space-md);
    border: var(--zp-border-hair) solid var(--zp-border-hairline);
    border-radius: var(--zp-radius-md);
    background: var(--zp-bg-surface);

    div {
      display: flex;
      justify-content: space-between;
      gap: var(--zp-space-sm);
      color: var(--zp-text-secondary);
      font-size: var(--zp-text-xs);
    }

    b {
      color: var(--zp-text-primary);
      font-weight: 650;
    }
  }

  &__rows {
    margin-top: var(--zp-space-md);
    border-top: var(--zp-border-hair) solid var(--zp-border-hairline);

    div {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--zp-space-sm);
      padding: var(--zp-space-xs) 0;
      border-bottom: var(--zp-border-hair) solid var(--zp-border-hairline);
      font-size: var(--zp-text-xs);
    }

    span {
      display: inline-flex;
      align-items: center;
      gap: var(--zp-space-xs);
      color: var(--zp-text-secondary);
    }

    b {
      min-width: 0;
      max-width: 140px;
      color: var(--zp-text-primary);
      font-weight: 650;
      text-align: right;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.v-dialog--fullscreen {
  .TaskRunView {
    min-height: calc(100dvh - 148px);
  }
}

@media (max-width: 1100px) {
  .TaskRunView__log {
    grid-template-columns: 220px minmax(0, 1fr);
  }

  .TaskRunConsole {
    grid-column: 2;
  }

  .TaskRunTelemetry {
    grid-column: 1 / -1;
    border-left: 0;
    border-top: var(--zp-border-hair) solid var(--zp-border-hairline);
  }
}

@media (max-width: 700px) {
  .TaskRunView {
    min-height: 0;

    &__status-strip,
    &__tabs {
      overflow-x: auto;
    }

    &__tab {
      min-width: 112px;
      padding: 0 var(--zp-space-sm);
    }

    &__log {
      display: flex;
      flex-direction: column;
      grid-template-columns: 1fr;
      overflow: auto;
    }

    &__actions {
      align-items: stretch;
      flex-direction: column;

      > div {
        width: 100%;
        justify-content: flex-end;
        flex-wrap: wrap;
      }
    }
  }

  .TaskRunTimeline {
    display: none;
  }

  .TaskRunConsole {
    grid-column: 1 / -1;
    flex: 0 0 auto;
    height: 420px;
    min-height: 420px;
  }

  .TaskRunConsole__toolbar {
    flex-wrap: wrap;
  }

  .TaskRunConsole__search {
    flex-basis: 100%;
  }

  .task-log-records {
    min-height: 320px;
  }
}
</style>

<script>
import axios from 'axios';
import TaskStatus from '@/components/TaskStatus.vue';
import socket from '@/socket';
import VirtualList from 'vue-virtual-scroll-list';
import TaskLogViewRecord from '@/components/TaskLogViewRecord.vue';
import ProjectMixin from '@/components/ProjectMixin';
import AnsibleStageView from '@/components/AnsibleStageView.vue';
import TaskDetails from '@/components/TaskDetails.vue';

export default {
  components: {
    TaskDetails, AnsibleStageView, TaskStatus, VirtualList,
  },

  mixins: [ProjectMixin],

  props: {
    item: Object,
    projectId: Number,
    systemInfo: Object,
    premiumFeatures: null,
    template: Object,
  },

  data() {
    return {
      tab: 0,
      itemComponent: TaskLogViewRecord,
      output: [],
      outputBuffer: [],
      user: {},
      autoScroll: true,
      paused: false,
      wrapLogs: true,
      logSearch: '',
      levelFilter: 'all',
    };
  },

  watch: {
    async itemId() {
      this.reset();
      await this.loadData();
    },

    async projectId() {
      this.reset();
      await this.loadData();
    },

    filteredOutput() {
      this.scheduleAutoScroll();
    },

    tab(value) {
      if (value === 0) {
        this.scheduleAutoScroll(true);
      }
    },
  },

  computed: {
    tabs() {
      return [
        {
          key: 'log',
          value: 0,
          label: this.$t('tabLog'),
          icon: 'terminal',
        },
        {
          key: 'details',
          value: 1,
          label: this.$t('tabDetails'),
          icon: 'list-checks',
        },
        {
          key: 'summary',
          value: 2,
          label: this.$t('tabSummary'),
          icon: 'chart-no-axes-column',
        },
      ];
    },

    itemId() {
      return this.item?.id;
    },

    isTaskStopped() {
      return [
        'stopped',
        'error',
        'success',
        'canceled',
        'rejected',
      ].includes(this.item.status);
    },

    rawLogURL() {
      return `${this.systemInfo?.web_host || ''}/api/project/${this.projectId}/tasks/${this.itemId}/raw_output`;
    },

    canStop() {
      return [
        'running',
        'stopping',
        'waiting',
        'starting',
        'waiting_confirmation',
        'confirmed',
        'rejected',
      ].includes(this.item.status);
    },

    filteredOutput() {
      const query = this.logSearch.trim().toLowerCase();
      return this.output.filter((record) => {
        const text = `${record.output || ''}`.toLowerCase();
        if (query && !text.includes(query)) return false;
        if (this.levelFilter === 'all') return true;
        return this.logLevel(record) === this.levelFilter;
      });
    },

    durationLabel() {
      return this.formatDuration(this.item.start, this.item.end);
    },

    startTimeLabel() {
      return this.formatTimeText(this.item.start);
    },

    userLabel() {
      return this.user?.name || this.item.user_name || this.$t('launchNotSelected');
    },

    telemetryRows() {
      return [
        {
          label: this.$t('template'),
          value: this.template?.name || this.item.tpl_alias || '—',
          icon: 'file-text',
        },
        {
          label: this.$t('taskId'),
          value: this.itemId == null ? '—' : `#${String(this.itemId).padStart(4, '0')}`,
          icon: 'hash',
          mono: true,
        },
        {
          label: this.$t('project'),
          value: `#${this.projectId}`,
          icon: 'box',
          mono: true,
        },
        {
          label: this.$t('inventory'),
          value: this.item.inventory || this.item.inventory_name || this.item.inventory_id || '—',
          icon: 'database',
        },
        {
          label: this.$t('environment'),
          value: this.item.environment_name || this.compactJson(this.item.environment) || '—',
          icon: 'layers',
        },
        {
          label: this.$t('branch'),
          value: this.item.get_branch || this.item.branch || this.template?.git_branch || '—',
          icon: 'git-branch',
        },
        {
          label: this.$t('repository'),
          value: this.item.repository || this.template?.repository_id || '—',
          icon: 'git-fork',
        },
        {
          label: this.$t('user'),
          value: this.userLabel,
          icon: 'user',
        },
      ];
    },

    executionStages() {
      const status = this.item.status;
      const failed = ['error', 'rejected', 'canceled'].includes(status);
      const finished = this.isTaskStopped;
      const runIcon = this.resolveStageIcon(failed, finished, 'play');
      const runVariant = this.resolveStageVariant(failed, finished, 'running');
      const finishIcon = this.resolveStageIcon(failed, finished, 'circle');
      const finishVariant = this.resolveStageVariant(failed, finished, 'pending');
      return [
        {
          key: 'queued',
          label: this.$t('stageQueued'),
          caption: this.$t('stageQueuedHint'),
          time: this.formatTimeText(this.item.created),
          icon: 'check',
          variant: this.item.created ? 'success' : 'pending',
        },
        {
          key: 'prepare',
          label: this.$t('stagePrepare'),
          caption: this.$t('stagePrepareHint'),
          time: this.formatTimeText(this.item.start),
          icon: ['waiting', 'starting'].includes(status) ? 'play' : 'check',
          variant: ['waiting', 'starting'].includes(status) ? 'running' : 'success',
        },
        {
          key: 'run',
          label: this.$t('stageRun'),
          caption: this.template?.name || this.item.message || this.$t('taskLogsStreaming'),
          time: this.formatTimeText(this.item.start),
          icon: runIcon,
          variant: runVariant,
        },
        {
          key: 'finish',
          label: this.$t('stageFinish'),
          caption: finished ? this.$t('stageFinishedHint') : this.$t('stageFinishHint'),
          time: this.formatTimeText(this.item.end),
          icon: finishIcon,
          variant: finishVariant,
        },
      ];
    },
  },

  async created() {
    this.outputInterval = setInterval(() => {
      this.$nextTick(() => {
        const len = this.outputBuffer.length;
        if (len === 0 || this.paused) {
          return;
        }

        const wasPinnedToBottom = this.isRecordsPinnedToBottom();

        this.output.push(...this.outputBuffer.splice(0, len));

        this.$nextTick(() => {
          if (wasPinnedToBottom || this.autoScroll) {
            this.autoScroll = true;
            this.scrollRecordsToBottom();
          }
        });
      });
    }, 1000);
    socket.addListener((data) => this.onWebsocketDataReceived(data));
    await this.loadData();
  },

  beforeDestroy() {
    clearInterval(this.outputInterval);
  },

  methods: {
    async confirmTask() {
      await axios({
        method: 'post',
        url: `/api/project/${this.projectId}/tasks/${this.itemId}/confirm`,
        responseType: 'json',
        data: {},
      });
    },

    async rejectTask() {
      await axios({
        method: 'post',
        url: `/api/project/${this.projectId}/tasks/${this.itemId}/reject`,
        responseType: 'json',
        data: {},
      });
    },

    async stopTask(force) {
      await axios({
        method: 'post',
        url: `/api/project/${this.projectId}/tasks/${this.itemId}/stop`,
        responseType: 'json',
        data: {
          force,
        },
      });
    },

    reset() {
      this.output = [];
      this.outputBuffer = [];
      this.user = {};
      this.autoScroll = true;
      this.logSearch = '';
      this.levelFilter = 'all';
    },

    onWebsocketDataReceived(data) {
      if (data.project_id !== this.projectId || data.task_id !== this.itemId) {
        return;
      }

      switch (data.type) {
        case 'update':
          Object.assign(this.item, {
            ...data,
            type: undefined,
          });
          break;
        case 'log':
          this.outputBuffer.push({
            ...data,
            id: data.time + data.output,
          });
          break;
        default:
          break;
      }
    },

    async loadData() {
      [
        this.output,
        this.user,
      ] = await Promise.all([
        (await axios({
          method: 'get',
          url: `/api/project/${this.projectId}/tasks/${this.itemId}/output`,
          responseType: 'json',
        })).data.map((item) => ({
          ...item,
          id: item.time + item.output,
        })),

        this.item.user_id ? (await axios({
          method: 'get',
          url: `/api/users/${this.item.user_id}`,
          responseType: 'json',
        })).data : null,
      ]);
      this.autoScroll = true;
      this.scheduleAutoScroll(true);
    },

    onRecordsScroll() {
      this.autoScroll = this.isRecordsPinnedToBottom();
    },

    getRecordsScroller() {
      return this.$refs.records?.$el || null;
    },

    isRecordsPinnedToBottom(tolerance = 24) {
      const scrollContainer = this.getRecordsScroller();
      if (!scrollContainer) {
        return true;
      }
      const distance = scrollContainer.scrollHeight
        - scrollContainer.clientHeight
        - scrollContainer.scrollTop;
      return distance <= tolerance;
    },

    scheduleAutoScroll(force = false) {
      if (this.tab !== 0 || (!force && !this.autoScroll)) {
        return;
      }
      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          this.scrollRecordsToBottom();
        });
      });
    },

    scrollRecordsToBottom() {
      const scrollContainer = this.getRecordsScroller();
      if (!scrollContainer) {
        return;
      }
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    },

    logLevel(record) {
      const text = `${record.output || ''}`.trim().toLowerCase();
      if (/^(fatal|failed|error|unreachable)/.test(text)) return 'error';
      if (/^(ok:|changed:|skipping:)/.test(text)) return 'ok';
      if (/^(task \[|play \[|task:)/.test(text)) return 'task';
      return 'info';
    },

    resolveStageIcon(failed, finished, activeIcon) {
      if (failed) return 'x';
      if (finished) return 'check';
      return activeIcon;
    },

    resolveStageVariant(failed, finished, activeVariant) {
      if (failed) return 'danger';
      if (finished) return 'success';
      return activeVariant;
    },

    formatTimeText(value) {
      if (!value) return '—';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '—';
      return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    },

    formatDuration(start, end) {
      if (!start) return '—';
      const startTime = new Date(start).getTime();
      const endTime = end ? new Date(end).getTime() : Date.now();
      if (Number.isNaN(startTime) || Number.isNaN(endTime)) return '—';
      const totalSeconds = Math.max(0, Math.floor((endTime - startTime) / 1000));
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      if (minutes < 60) {
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      }
      const hours = Math.floor(minutes / 60);
      return `${String(hours).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    },

    compactJson(value) {
      if (!value || value === '{}') return '';
      if (typeof value !== 'string') return JSON.stringify(value);
      try {
        return Object.keys(JSON.parse(value)).length > 0 ? `${Object.keys(JSON.parse(value)).length} items` : '';
      } catch (e) {
        return value;
      }
    },
  },
};
</script>
